import React, { useState, useEffect } from 'react';
import { 
  PhoneCall, X, Phone, PhoneOff, Video, Clock, 
  CheckCircle, XCircle, Loader, Users, Bell
} from 'lucide-react';
import API_URL from '../config/API_URL';

export const LiveCallModal = ({ 
  isOpen, 
  onClose, 
  familyId, 
  currentUser, 
  onCallInitiated,
  socket,
}) => {
  const [readers, setReaders] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [dialingUser, setDialingUser] = useState(null);
  const [callRequests, setCallRequests] = useState(new Map());

  useEffect(() => {
    if (isOpen && familyId) {
      fetchReaders();
      checkOnlineStatus();
    }
  }, [isOpen, familyId]);

  // Listen for call accepted event
  useEffect(() => {
    if (!socket || !isOpen) return;

    const handleCallAccepted = (data) => {
      console.log('✅ Call accepted:', data);
      
      // Find which reader accepted
      const acceptedReaderId = [...callRequests.entries()]
        .find(([_, req]) => req.roomId === data.room_id)?.[0];
      
      if (acceptedReaderId) {
        // Clear the pending request
        setCallRequests(prev => {
          const updated = new Map(prev);
          updated.delete(acceptedReaderId);
          return updated;
        });
        
        // Notify parent component to navigate to video room
        if (onCallInitiated) {
          onCallInitiated({
            roomId: data.room_id,
            participants: data.participants
          });
        }
        
        // Close modal
        onClose();
      }
    };

    const handleCallDeclined = (data) => {
      console.log('❌ Call declined:', data);
      
      // Find which reader declined
      const declinedReaderId = [...callRequests.entries()]
        .find(([_, req]) => req.roomId === data.room_id)?.[0];
      
      if (declinedReaderId) {
        setCallRequests(prev => {
          const updated = new Map(prev);
          updated.set(declinedReaderId, {
            ...updated.get(declinedReaderId),
            status: 'declined'
          });
          return updated;
        });
        setDialingUser(null);
      }
    };

    socket.on('call-accepted', handleCallAccepted);
    socket.on('call-declined', handleCallDeclined);

    return () => {
      socket.off('call-accepted', handleCallAccepted);
      socket.off('call-declined', handleCallDeclined);
    };
  }, [socket, isOpen, callRequests, onCallInitiated, onClose]);

  console.log('currentgUISER : ', currentUser.role)
  const claudeIdiotVAriable =  currentUser.role;
  const fetchReaders = async () => {
    setLoading(true);
    if(claudeIdiotVAriable == 'parent'){
      try {
        const response = await fetch(`${API_URL}/api/family/${familyId}`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log('data: ', data)
          const readerMembers = (data.members || []).filter(m => m.family_role === 'reader')
            .map(member => ({
              ...member,
              associatedFamilyId: familyId // Add family ID for parents
            }));
          setReaders(readerMembers);
        }
      } catch (error) {
        console.error('Failed to fetch readers:', error);
      } finally {
        setLoading(false);
      }
    } else {
      try {
        const response = await fetch(`${API_URL}/api/family/my-families`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log('data: ', data)
          // Map creators WITH their corresponding family IDs
          const readerMembers = data.map(family => ({
            ...family.creator,
            associatedFamilyId: family.id // Store the family ID with each creator
          }));
          setReaders(readerMembers);
        }
      } catch (error) {
        console.error('Failed to fetch readers:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  const checkOnlineStatus = async () => {
    try {
      if (claudeIdiotVAriable === 'parent') {
        // Parents check online status within their family
        const response = await fetch(`${API_URL}/api/family/${familyId}/online-users`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          setOnlineUsers(new Set(data.online_user_ids));
        }
      } else {
        // Readers need to check online status across all families they're part of
        const response = await fetch(`${API_URL}/api/family/my-families`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const families = await response.json();
          
          // Collect all online user IDs from all families
          const allOnlineUserIds = new Set();
          
          // Fetch online users for each family
          await Promise.all(
            families.map(async (family) => {
              const onlineResponse = await fetch(`${API_URL}/api/family/${family.id}/online-users`, {
                credentials: 'include'
              });
              
              if (onlineResponse.ok) {
                const onlineData = await onlineResponse.json();
                onlineData.online_user_ids.forEach(id => allOnlineUserIds.add(id));
              }
            })
          );
          
          setOnlineUsers(allOnlineUserIds);
        }
      }
    } catch (error) {
      console.error('Failed to check online status:', error);
    }
  };

  const handleDialUser = async (reader) => {
    setDialingUser(reader.id);
    
    try {
      const response = await fetch(`${API_URL}/api/video-call/initiate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          receiver_id: reader.id,
          family_id: reader.associatedFamilyId
        })
      });

      const data = await response.json();
      
      if (data.success) {
        // Add to pending requests
        setCallRequests(prev => new Map(prev).set(reader.id, {
          status: 'pending',
          roomId: data.room_id,
          callId: data.call_id,
          timestamp: Date.now()
        }));
        
        // Set timeout for no answer
        setTimeout(() => {
          setCallRequests(prev => {
            const updated = new Map(prev);
            const request = updated.get(reader.id);
            if (request && request.status === 'pending') {
              updated.set(reader.id, { ...request, status: 'no_answer' });
            }
            return updated;
          });
          setDialingUser(null);
        }, 30000); // 30 second timeout
      }
    } catch (error) {
      console.error('Failed to initiate call:', error);
      setDialingUser(null);
    }
  };

  const handleCancelCall = async (readerId) => {
    try {
      const request = callRequests.get(readerId);
      if (request) {
        await fetch(`${API_URL}/api/video-call/cancel`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            room_id: request.roomId
          })
        });
      }
      
      setCallRequests(prev => {
        const updated = new Map(prev);
        updated.delete(readerId);
        return updated;
      });
      setDialingUser(null);
    } catch (error) {
      console.error('Failed to cancel call:', error);
    }
  };

  if (!isOpen) return null;

  const getReaderStatus = (readerId) => {
    const request = callRequests.get(readerId);
    if (request) return request.status;
    return onlineUsers.has(readerId) ? 'online' : 'offline';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online': return '#10b981';
      case 'offline': return '#6b7280';
      case 'pending': return '#f59e0b';
      case 'no_answer': return '#ef4444';
      case 'declined': return '#ef4444';
      default: return '#6b7280';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'online': return <CheckCircle size={16} />;
      case 'offline': return <XCircle size={16} />;
      case 'pending': return <Loader size={16} className="animate-spin" />;
      case 'no_answer': return <PhoneOff size={16} />;
      case 'declined': return <XCircle size={16} />;
      default: return null;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'online': return 'Available';
      case 'offline': return 'Offline';
      case 'pending': return 'Calling...';
      case 'no_answer': return 'No Answer';
      case 'declined': return 'Declined';
      default: return 'Unknown';
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '1rem'
    }}>
      <div style={{
        background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)',
        borderRadius: '24px',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.5)',
        border: '2px solid #4c1d95'
      }}>
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: '2px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '48px',
              height: '48px',
              background: 'linear-gradient(135deg, #10b981, #059669)',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)'
            }}>
              <PhoneCall size={24} style={{ color: 'white' }} />
            </div>
            <div>
              <h2 style={{
                margin: 0,
                fontSize: '24px',
                fontWeight: '700',
                color: 'white'
              }}>
                Start Live Call
              </h2>
              <p style={{
                margin: '4px 0 0 0',
                fontSize: '14px',
                color: 'rgba(255, 255, 255, 0.7)'
              }}>
                Call your family readers & grandparents
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.1)',
              border: 'none',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'white',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)'}
            onMouseLeave={(e) => e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)'}
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          padding: '24px'
        }}>
          {loading ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '40px',
              color: 'rgba(255, 255, 255, 0.7)'
            }}>
              <Loader size={24} className="animate-spin" style={{ marginRight: '12px' }} />
              Loading family members...
            </div>
          ) : readers.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px',
              color: 'rgba(255, 255, 255, 0.7)'
            }}>
              <Users size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
              <p style={{ fontSize: '16px', margin: 0 }}>
                No readers or grandparents found in your family
              </p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {readers.map((reader) => {
                const status = getReaderStatus(reader.id);
                const request = callRequests.get(reader.id);
                const isDialing = dialingUser === reader.id;
                const canDial = !request;

                return (
                  <div
                    key={reader.id}
                    style={{
                      background: 'rgba(255, 255, 255, 0.05)',
                      backdropFilter: 'blur(10px)',
                      borderRadius: '16px',
                      padding: '16px',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {/* Reader Info */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                      <div style={{ position: 'relative' }}>
                        <div style={{
                          width: '56px',
                          height: '56px',
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '24px',
                          fontWeight: '700',
                          color: 'white',
                          textTransform: 'uppercase'
                        }}>
                          {reader.first_name.charAt(0)}
                        </div>
                        <div style={{
                          position: 'absolute',
                          bottom: '2px',
                          right: '2px',
                          width: '16px',
                          height: '16px',
                          borderRadius: '50%',
                          background: getStatusColor(status),
                          border: '3px solid #1e1b4b',
                          boxShadow: status === 'online' ? '0 0 8px rgba(16, 185, 129, 0.8)' : 'none'
                        }} />
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <h3 style={{
                          margin: '0 0 4px 0',
                          fontSize: '18px',
                          fontWeight: '600',
                          color: 'white',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}>
                          {reader.first_name} {reader.last_name}
                        </h3>
                        <div style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          color: getStatusColor(status),
                          fontSize: '14px'
                        }}>
                          {getStatusIcon(status)}
                          <span>{getStatusText(status)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Action Button */}
                    <div>
                      {request && request.status === 'pending' ? (
                        <button
                          onClick={() => handleCancelCall(reader.id)}
                          style={{
                            padding: '12px 20px',
                            background: '#ef4444',
                            border: 'none',
                            borderRadius: '12px',
                            color: 'white',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <PhoneOff size={16} />
                          Cancel
                        </button>
                      ) : request && (request.status === 'no_answer' || request.status === 'declined') ? (
                        <button
                          onClick={() => handleDialUser(reader)}
                          style={{
                            padding: '12px 20px',
                            background: 'rgba(245, 158, 11, 0.2)',
                            border: '1px solid #f59e0b',
                            borderRadius: '12px',
                            color: '#fbbf24',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          Try Again
                        </button>
                      ) : (
                        <button
                          onClick={() => canDial && handleDialUser(reader)}
                          disabled={!canDial || isDialing}
                          style={{
                            padding: '12px 20px',
                            background: canDial && !isDialing 
                              ? 'linear-gradient(135deg, #10b981, #059669)' 
                              : 'rgba(255, 255, 255, 0.1)',
                            border: 'none',
                            borderRadius: '12px',
                            color: 'white',
                            fontSize: '14px',
                            fontWeight: '600',
                            cursor: canDial && !isDialing ? 'pointer' : 'not-allowed',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            opacity: canDial && !isDialing ? 1 : 0.5,
                            transition: 'all 0.2s ease',
                            boxShadow: canDial && !isDialing 
                              ? '0 4px 12px rgba(16, 185, 129, 0.3)' 
                              : 'none'
                          }}
                        >
                          <Phone size={16} />
                          Call
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer Info */}
        <div style={{
          padding: '16px 24px',
          borderTop: '2px solid rgba(255, 255, 255, 0.1)',
          background: 'rgba(0, 0, 0, 0.2)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: '13px'
          }}>
            <Bell size={14} />
            <span>
              They'll receive a notification when you call. Calls timeout after 30 seconds.
            </span>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};
